import { css } from "styled-system/css";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageShell } from "@/components/admin/admin-page-shell";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { EmptyState } from "@/components/admin/empty-state";
import { useToast } from "@/hooks/useToast";
import { slugify } from "@/lib/slugify";

type ReferralCodeRow = {
  id: string;
  code: string;
  ownerLabel: string;
  active: boolean;
  createdBy: string | null;
  createdAt: string;
  registeredCount: number;
  payingCount: number;
  conversionRate: number;
};

function suggestCode(label: string): string {
  const firstWord = label.trim().split(/\s+/)[0] ?? "";
  const base = slugify(firstWord).replace(/-/g, "").toUpperCase().slice(0, 10) || "REF";
  const suffix = Math.floor(10 + Math.random() * 90);
  return `${base}${suffix}`;
}

async function parseErrorMessage(r: Response, fallback: string): Promise<string> {
  const j = (await r.json().catch(() => ({}))) as { error?: string };
  if (j.error && j.error.length < 160 && !/[{}[\]]/.test(j.error)) return j.error;
  return fallback;
}

export function AdminReferralCodes() {
  const { refresh } = useAuth();
  const toast = useToast();
  const [codes, setCodes] = useState<ReferralCodeRow[] | null>(null);
  const [ownerLabel, setOwnerLabel] = useState("");
  const [code, setCode] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await fetchWithSessionSync("/api/admin/referral-codes", { credentials: "include" }, refresh);
    if (r.ok) setCodes((await r.json()) as ReferralCodeRow[]);
  }, [refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  function onOwnerLabelChange(value: string) {
    setOwnerLabel(value);
    if (!codeTouched) setCode(suggestCode(value));
  }

  async function submit() {
    if (!ownerLabel.trim()) {
      toast.error("Ingresá quién comparte el código");
      return;
    }
    if (!code.trim()) {
      toast.error("Ingresá un código");
      return;
    }
    setBusy(true);
    const r = await fetchWithSessionSync(
      "/api/admin/referral-codes",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim(), ownerLabel: ownerLabel.trim() }),
      },
      refresh,
    );
    setBusy(false);
    if (!r.ok) {
      toast.error(await parseErrorMessage(r, "No pudimos crear el código."));
      return;
    }
    toast.success("Código creado.");
    setOwnerLabel("");
    setCode("");
    setCodeTouched(false);
    void load();
  }

  async function toggleActive(row: ReferralCodeRow) {
    setTogglingId(row.id);
    const r = await fetchWithSessionSync(
      `/api/admin/referral-codes/${row.id}`,
      {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: !row.active }),
      },
      refresh,
    );
    setTogglingId(null);
    if (!r.ok) {
      toast.error(await parseErrorMessage(r, "No pudimos actualizar el código."));
      return;
    }
    void load();
  }

  if (!codes) {
    return (
      <AdminPageShell title="Códigos de referido">
        <LoadingFrame variant="light" minHeight={320} />
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      title="Códigos de referido"
      description="Generá códigos para compartir con contactos. El código solo sirve para atribuir quién trajo cada cuenta — no otorga días extra de prueba."
    >
      <div className={css({ display: "flex", flexDir: "column", gap: "6" })}>
        {/* Form crear */}
        <div className={css({ p: "6", rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card" })}>
          <p className={css({ fontWeight: "semibold", color: "fg.DEFAULT", mb: "4" })}>Nuevo código</p>
          <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", md: "2fr 1fr" }, gap: "4" })}>
            <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
              <span className={css({ fontWeight: "medium", color: "fg.muted" })}>¿Quién lo comparte?</span>
              <input
                value={ownerLabel}
                onChange={(e) => onOwnerLabelChange(e.target.value)}
                placeholder="Juan - contactos kinesiólogos"
                className={css({ h: "9", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "sm" })}
              />
            </label>
            <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
              <span className={css({ fontWeight: "medium", color: "fg.muted" })}>Código</span>
              <input
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setCodeTouched(true);
                }}
                placeholder="JUAN10"
                className={css({ h: "9", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "sm", textTransform: "uppercase" })}
              />
            </label>
          </div>
          <div className={css({ mt: "5" })}>
            <Button onClick={() => void submit()} disabled={busy}>
              {busy ? "Creando…" : "Crear código"}
            </Button>
          </div>
        </div>

        {/* Listado */}
        {codes.length === 0 ? (
          <EmptyState title="Sin códigos todavía" description="Creá el primero desde el formulario de arriba." icon="🔗" />
        ) : (
          <div className={css({ overflowX: "auto", rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card" })}>
            <table className={css({ w: "full", borderCollapse: "collapse", fontSize: "sm" })}>
              <thead>
                <tr className={css({ bg: "bg.muted", textAlign: "left" })}>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Código</th>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Quién lo comparte</th>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Registrados</th>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Pagando</th>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Conversión</th>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Estado</th>
                  <th className={css({ py: "3", px: "4" })} />
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.id} className={css({ borderTopWidth: "1px", borderColor: "border.DEFAULT", _hover: { bg: "bg.muted" } })}>
                    <td className={css({ py: "3", px: "4", fontWeight: "semibold" })}>{c.code}</td>
                    <td className={css({ py: "3", px: "4", color: "fg.muted" })}>{c.ownerLabel}</td>
                    <td className={css({ py: "3", px: "4" })}>{c.registeredCount}</td>
                    <td className={css({ py: "3", px: "4" })}>{c.payingCount}</td>
                    <td className={css({ py: "3", px: "4" })}>{Math.round(c.conversionRate * 100)}%</td>
                    <td className={css({ py: "3", px: "4" })}>
                      <button
                        type="button"
                        onClick={() => void toggleActive(c)}
                        disabled={togglingId === c.id}
                        className={css({ bg: "transparent", border: "none", p: "0", cursor: "pointer" })}
                      >
                        <Badge tone={c.active ? "success" : "neutral"}>{c.active ? "Activo" : "Inactivo"}</Badge>
                      </button>
                    </td>
                    <td className={css({ py: "3", px: "4", textAlign: "right" })}>
                      <Link to={`/admin/referral-codes/${c.id}`}>
                        <Button variant="secondary" size="sm">
                          Ver cuentas
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminPageShell>
  );
}
